import React, { useState } from 'react';
import {
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import MoreVert from '@mui/icons-material/MoreVert';
import OpenInNew from '@mui/icons-material/OpenInNew';
import ContentCopy from '@mui/icons-material/ContentCopy';
import FileCopy from '@mui/icons-material/FileCopy';
import Edit from '@mui/icons-material/Edit';
import DriveFileMove from '@mui/icons-material/DriveFileMove';
import Delete from '@mui/icons-material/Delete';
import { Bookmark } from '../types/bookmarks';
import { useBookmarksModel } from '../hooks/useBookmarksModel';
import { useCacheStore } from '../hooks/data/useCacheStore';
import { normalizeUrl } from '../utils/bookmarkUtils';
import { ConfirmDialog } from './ConfirmDialog';
import { CategoryPicker } from './CategoryPicker';

interface BookmarkActionsMenuProps {
  bookmark: Bookmark;
  /** Called when the user chooses "Edit" — parent promotes the row to edit mode. */
  onEdit: () => void;
  /** Icon button size, defaults to small. */
  iconSize?: number;
}

/**
 * Overflow (⋮) context menu exposing every bookmark action: Open, Copy URL,
 * Copy Notes, Edit, Move, Delete. Instantiates the bookmark model so Move and
 * Delete work without entering edit mode. Shared by card, compact, and list views.
 */
const BookmarkActionsMenu: React.FC<BookmarkActionsMenuProps> = ({
  bookmark,
  onEdit,
  iconSize = 16,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  // Target chosen in the picker, awaiting move confirmation.
  // undefined = no pending move; null = move to Root.
  const [pendingMoveTargetId, setPendingMoveTargetId] = useState<
    string | null | undefined
  >(undefined);
  const cacheStore = useCacheStore();

  // The model needs edit lifecycle callbacks. Edit is delegated to the parent;
  // move/delete resolve internally and call onEditEnd (a no-op here).
  const {
    confirmDeleteOpen,
    categoryPickerOpen,
    disabledCategoryIds,
    isOnline,
    plainTextNotes,
    handleDeleteClick,
    handleConfirmDelete,
    handleCancelDelete,
    handleMoveClick,
    handleMoveSelect,
    handleMoveCancel,
    handleCopyUrl,
    handleCopyNotes,
  } = useBookmarksModel({
    bookmark,
    onEditStart: () => {},
    onEditEnd: () => {},
  });

  const open = Boolean(anchorEl);
  const href = normalizeUrl(bookmark.bookmark_url);

  const targetName =
    pendingMoveTargetId === null
      ? 'Root'
      : cacheStore
          .getCategories()
          ?.find((c) => c.category_id === pendingMoveTargetId)?.category_name ??
        'that folder';

  // Picker selection no longer moves immediately — it stages the target and
  // opens a confirmation dialog.
  const handlePickerSelect = (categoryId: string | null) => {
    handleMoveCancel(); // close the picker
    setPendingMoveTargetId(categoryId);
  };

  const handleConfirmMove = () => {
    const target = pendingMoveTargetId;
    setPendingMoveTargetId(undefined);
    if (target !== undefined) {
      handleMoveSelect(target);
    }
  };

  const handleCancelMove = () => {
    setPendingMoveTargetId(undefined);
  };

  const handleOpenMenu = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const runAction = (action: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    handleCloseMenu();
    action();
  };

  const handleOpenLink = () => {
    if (href) {
      window.open(href, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    // Stop clicks originating anywhere in this menu (including portaled menu
    // items / dialogs that re-target here) from bubbling to the row's onClick,
    // which would otherwise open the preview drawer after a move.
    <span
      onClick={(e) => e.stopPropagation()}
      style={{ display: 'inline-flex' }}
    >
      <IconButton
        size="small"
        onClick={handleOpenMenu}
        aria-label="Bookmark actions"
        sx={{
          // Finger-friendly on touch (44x44), tighter on pointer devices.
          p: { xs: 1.25, sm: 0.5 },
          minWidth: { xs: 44, sm: 'auto' },
          minHeight: { xs: 44, sm: 'auto' },
        }}
      >
        <MoreVert sx={{ fontSize: iconSize }} />
      </IconButton>

      <Menu anchorEl={anchorEl} open={open} onClose={handleCloseMenu}>
        <MenuItem onClick={runAction(handleOpenLink)} disabled={!href}>
          <ListItemIcon>
            <OpenInNew fontSize="small" />
          </ListItemIcon>
          <ListItemText>Open</ListItemText>
        </MenuItem>
        <MenuItem onClick={runAction(handleCopyUrl)}>
          <ListItemIcon>
            <ContentCopy fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy URL</ListItemText>
        </MenuItem>
        {plainTextNotes && (
          <MenuItem onClick={runAction(handleCopyNotes)}>
            <ListItemIcon>
              <FileCopy fontSize="small" />
            </ListItemIcon>
            <ListItemText>Copy Notes</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={runAction(onEdit)}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={runAction(handleMoveClick)} disabled={!isOnline}>
          <ListItemIcon>
            <DriveFileMove fontSize="small" />
          </ListItemIcon>
          <ListItemText>Move</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={runAction(handleDeleteClick)}
          disabled={!isOnline}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      <ConfirmDialog
        open={confirmDeleteOpen}
        title="Delete Bookmark"
        message={`Are you sure you want to delete "${bookmark.bookmark_name}"?`}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      <CategoryPicker
        open={categoryPickerOpen}
        title="Move Bookmark"
        disabledIds={disabledCategoryIds}
        currentLocationId={bookmark.category_id || null}
        onSelect={handlePickerSelect}
        onCancel={handleMoveCancel}
      />

      <ConfirmDialog
        open={pendingMoveTargetId !== undefined}
        title="Move Bookmark"
        message={`Move "${bookmark.bookmark_name}" to "${targetName}"?`}
        confirmText="Move"
        onConfirm={handleConfirmMove}
        onCancel={handleCancelMove}
      />
    </span>
  );
};

export default BookmarkActionsMenu;
